import { ApiError, api, getServerTimeOffsetMs } from '@/api/client';
import { queryClient } from '@/queryClient';
import { useShellNav } from '@/stores/shellNav';
import { toast } from '@/stores/toast';
import { onWindowMessage, tellOtherWindows } from '@/stores/channel';
import { setBackOnlineWords, useConnection } from '@/stores/connection';
import {
  clearSentOffline,
  enqueue,
  makeOps,
  markKeptOffline,
  readOutbox,
  sendAgain,
  tokenise,
  withdraw,
  withEverythingOn,
  type NewOperation,
  type Operation,
  type OutboxHolder,
} from './outbox';
import { idOf, noConnection, sendPass, statusOf, useOutboxStatus, type Pass, type Send } from './sender';

/**
 * Keeps the outbox moving (.docs/023, step 4).
 *
 * One tab sends; the browser's Web Locks decide which, and when that tab
 * closes the next one waiting takes over. Every tab keeps the status in step,
 * because every tab shows it.
 *
 * It sends straight after work is saved, when the connection comes back, and
 * every minute while anything waits. A dead line or a server fault is waited
 * out with a growing pause, five seconds up to five minutes, and never shown
 * as a failure.
 */

const BACKOFF_START_MS = 5_000;
const BACKOFF_MAX_MS = 5 * 60_000;
const EVERY_MS = 60_000;

const send: Send = (method, path, body, headers) => api.send(method, path, body, headers);

let holder: OutboxHolder | null = null;
let stopping: AbortController | null = null;
let wake: (() => void) | null = null;
/**
 * Set by a nudge that arrives while a pass is running rather than sleeping.
 * Without it, work saved mid-pass would wait for the next tick: the pass had
 * already read the list, and there was no sleep yet to cut short.
 */
let poked = false;

function nudge(): void {
  poked = true;
  wake?.();
}

/** Starts sending this person's work. Called when their copy opens. */
export function startOutbox(store: OutboxHolder): void {
  stopOutbox();
  holder = store;
  const controller = new AbortController();
  stopping = controller;
  void refreshStatus(null);

  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks) {
    // Waits its turn behind any other tab of the same person; a tab that is
    // stopped stops waiting.
    void locks
      .request(`poultry.sender.${store.userId}`, { signal: controller.signal }, () => loop(store, controller.signal))
      .catch(() => undefined);
  } else {
    void loop(store, controller.signal);
  }
}

/** Stops sending. At sign-out; the work stays in the copy for its owner. */
export function stopOutbox(): void {
  stopping?.abort();
  stopping = null;
  holder = null;
  wake?.();
  useOutboxStatus.setState({ waiting: 0, refused: 0, held: null, lastSentAt: null, sending: false });
}

/** Sends now rather than at the next tick, here or in whichever tab sends. */
export function pokeOutbox(): void {
  nudge();
  tellOtherWindows({ type: 'outbox' });
}

let passTurn: Promise<unknown> = Promise.resolve();

/**
 * One pass, one at a time across every tab: the sending tab's own, and the
 * pass a tab runs for work just saved there. Two at once would send the same
 * work twice, and the second would be told it is "still being sent".
 */
function runPass(store: OutboxHolder): Promise<Pass> {
  const go = () => sendPass(store, send).catch((): Pass => ({ sent: 0, stopped: 'wait' }));
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks) return locks.request(`poultry.pass.${store.userId}`, go) as Promise<Pass>;
  const turn = passTurn.then(go, go);
  passTurn = turn;
  return turn;
}

async function loop(store: OutboxHolder, signal: AbortSignal): Promise<void> {
  let backoff = BACKOFF_START_MS;
  while (!signal.aborted) {
    poked = false;
    let pass: Pass;
    // Said only while there is something to send and a line to send it on.
    if (useOutboxStatus.getState().waiting > 0 && useConnection.getState().online) useOutboxStatus.setState({ sending: true });
    pass = await runPass(store);
    useOutboxStatus.setState({ sending: false });
    if (signal.aborted) return;
    await refreshStatus(pass.stopped);
    if (pass.sent > 0) tellOtherWindows({ type: 'outbox' });
    tellWhatHappened(pass, new Set());

    const waiting = useOutboxStatus.getState().waiting > 0;
    let pause = waiting ? EVERY_MS : 24 * 60 * 60_000;
    if (pass.stopped === 'wait') {
      pause = backoff;
      backoff = Math.min(backoff * 2, BACKOFF_MAX_MS);
    } else {
      backoff = BACKOFF_START_MS;
    }
    // Something was saved or the line came back while that pass ran: go again
    // now. A pass that stopped on a dead line still waits its pause.
    if (poked && pass.stopped !== 'wait') continue;
    // Something went and more waits: what waits may have been standing on it
    // (a receipt on the supplier it names), so it can go now too. Each such
    // pass sends something, so this ends.
    if (pass.sent > 0 && pass.stopped === null && waiting) continue;
    await sleep(pause, signal);
  }
}

/**
 * Work its form already reported was answered while the form waited. Anything
 * answered later — sent once the line came back, or refused then — nobody has
 * been told about, so it is said here.
 */
const ANSWERED_IN_FORM_MS = 6_000;

/** Reloads every item for the pickers. Set by the warm-up, which owns that list. */
let afterReferenceSent: () => void = () => {};
export function setAfterReferenceSent(reload: () => void): void {
  afterReferenceSent = reload;
}

function tellWhatHappened(pass: Pass, inForm: ReadonlySet<string>): void {
  const now = Date.now() + getServerTimeOffsetMs();
  const late = (op: Operation) => !inForm.has(op.id) && now - op.createdAt > ANSWERED_IN_FORM_MS;

  // One piece of work per document: a post that went with its create is the same work.
  const sent = (pass.sentOps ?? []).filter((op) => late(op) && !(op.kind.endsWith('.post') && op.dependsOn));
  if (sent.length > 0) {
    const numbers = sent
      .map((op) => (op.result as { documentNo?: string; code?: string } | null)?.documentNo ?? (op.result as { code?: string } | null)?.code)
      .filter((n): n is string => Boolean(n));
    const shown = numbers.slice(0, 4).join(', ') + (numbers.length > 4 ? ` and ${numbers.length - 4} more` : '');
    toast.success(
      `${sent.length === 1 ? 'Work saved on this PC was' : `${sent.length} pieces of work saved on this PC were`} sent to the server${shown ? `: ${shown}` : ''}.`,
      // Where it is listed, with its number, until somebody clears it.
      sent.some((op) => op.keptOffline) ? { label: 'See it', onClick: () => useShellNav.getState().navigate('home-stuck') } : undefined,
    );
    // The lists read again, with the real numbers and codes in place of "Not sent yet".
    void queryClient.invalidateQueries({ queryKey: ['inv'] });
    if (sent.some((op) => op.lane === 'reference')) {
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      afterReferenceSent();
    }
  }

  const refused = (pass.refusedOps ?? []).filter(late);
  if (refused.length > 0) {
    toast.error(
      refused.length === 1
        ? `The server would not take "${refused[0]!.describes}": ${refused[0]!.lastError ?? 'refused'}`
        : `The server would not take ${refused.length} pieces of saved work.`,
      { label: 'Open', onClick: () => useShellNav.getState().navigate('home-stuck') },
    );
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      wake = null;
      resolve();
    }
    wake = done;
    signal.addEventListener('abort', done);
  });
}

async function refreshStatus(held: Pass['stopped'] | undefined): Promise<void> {
  const store = holder;
  if (!store) return;
  const ops = await readOutbox(store).catch(() => [] as Operation[]);
  useOutboxStatus.setState({
    ...statusOf(ops),
    ...(held === undefined ? {} : { held }),
  });
}

// Another tab saved or sent something: send now if this tab is the sender,
// and show the new figures either way.
onWindowMessage((message) => {
  if (message.type !== 'outbox') return;
  nudge();
  void refreshStatus(undefined);
});

setBackOnlineWords(() => {
  const { waiting } = useOutboxStatus.getState();
  return waiting > 0 ? ` Sending ${waiting === 1 ? 'the work' : `${waiting} pieces of work`} saved on this PC…` : '';
});

// The line is back: whatever waits goes now, not at the next tick.
useConnection.subscribe((state, previous) => {
  if (state.online && !previous.online) nudge();
});

// ------------------------------------------------------------- saving work

export interface Saved {
  /**
   * `sent`: the server took all of it. `refused`: it said no to some.
   * `waiting`: no answer yet, and the work is safe and will go by itself.
   * `not-kept`: no answer, and nowhere to keep it — nothing is saved.
   */
  outcome: 'sent' | 'refused' | 'waiting' | 'not-kept';
  /** The operations as they now stand, in the order given. */
  ops: Operation[];
}

/**
 * A list in memory, for a browser that will not keep a copy: work waits in it
 * while the page is open, and is lost with the page.
 */
export function memoryHolder(): OutboxHolder {
  const kept = new Map<string, unknown>();
  return {
    userId: 'memory',
    read: async <T,>(name: string) => (kept.get(name) as T | undefined) ?? null,
    write: async (name: string, value: unknown) => {
      kept.set(name, value);
    },
  };
}

/** How long a save sent straight to the server waits for its answer. */
const ANSWER_MS = 15_000;
/** How long older work saved offline is given to go first. */
const OLDER_FIRST_MS = 10_000;

/** Said on the form when the server was reached but gave no answer, or failed. */
export const NO_ANSWER = 'The server did not answer. Check the list before saving again.';
const BUSY = 'The server is busy. Wait a moment and save again.';

/**
 * Saves work.
 *
 * With a line it goes straight to the server, as any save does, and the
 * screen shows the answer: the document's number, or the refusal on the form.
 * It is never kept on this PC, so it never shows as waiting. A server that is
 * reached but does not answer is an error on the form too, and nothing is kept.
 *
 * When the server cannot be reached at all, it is kept on this PC under the
 * same retry key, listed on Offline work, and sent by itself when the server
 * can be reached again. Only the attempt decides: the browser's own "offline"
 * means no internet, and a server on this PC or the farm's own network still
 * answers without one.
 *
 * Older work saved offline goes first, so things reach the server in the
 * order they were done. In a browser that keeps no copy, the work is sent from
 * memory and must be answered now.
 */
export async function saveWork(batch: readonly NewOperation[], waitMs = WAIT_MS): Promise<Saved> {
  const store = holder ?? memoryHolder();

  if (store !== holder) {
    // Nobody signed in, so nothing sends later: answered now, or not saved.
    const ids = await enqueue(store, batch);
    await sendPass(store, send);
    const now = outcomeOf(await readOutbox(store), ids);
    return now.outcome === 'waiting' ? { ...now, outcome: 'not-kept' } : now;
  }

  let kept = await readOutbox(store);
  if (kept.some((op) => op.state === 'waiting' || op.state === 'sending')) {
    const pass = await Promise.race([
      runPass(store),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), OLDER_FIRST_MS)),
    ]);
    if (pass) {
      tellWhatHappened(pass, new Set());
      if (pass.sent > 0 || (pass.refusedOps?.length ?? 0) > 0) tellOtherWindows({ type: 'outbox' });
      await refreshStatus(pass.stopped);
    }
    kept = await readOutbox(store);
  }

  const ops = makeOps(batch);
  const sent = new Map(kept.filter((op) => op.state === 'sent' && op.resultId).map((op) => [op.id, op.resultId!]));
  // It names something saved on this PC that has not gone yet — a supplier
  // typed in with no line, say — so it can only go after it, from the outbox.
  if (ops.some((op) => tokenise(op.path, sent).refs.length > 0 || tokenise(op.body, sent).refs.length > 0)) {
    return awaitWork(await enqueue(store, batch), waitMs);
  }
  return sendStraight(store, batch, ops, sent);
}

/** Sends a batch straight to the server, in order, each on the id the one before got. */
async function sendStraight(
  store: OutboxHolder,
  batch: readonly NewOperation[],
  ops: Operation[],
  sent: ReadonlyMap<string, string>,
): Promise<Saved> {
  const made = new Map<string, string>();
  for (let i = 0; i < ops.length; i++) {
    const op = ops[i]!;
    let path = tokenise(op.path, sent).value as string;
    if (op.dependsOn) path = path.replace('{dep}', encodeURIComponent(made.get(op.dependsOn) ?? ''));
    const body = op.body === undefined ? undefined : tokenise(op.body, sent).value;

    const calling = new AbortController();
    let late = false;
    const timer = setTimeout(() => {
      late = true;
      calling.abort();
    }, ANSWER_MS);
    op.attempts = 1;
    try {
      const result = await api.send(
        op.method,
        path,
        body,
        { 'Idempotency-Key': op.id, 'X-Client-Recorded-At': new Date(op.createdAt).toISOString() },
        calling.signal,
      );
      const resultId = idOf(result);
      Object.assign(op, { state: 'sent', path, sentAt: Date.now() + getServerTimeOffsetMs(), result, resultId });
      if (resultId) made.set(op.id, resultId);
    } catch (error) {
      // The line is down: this and what follows are kept, to go when it is back.
      if (!late && noConnection(error)) return keepRest(store, batch, ops, i, made);
      const status = error instanceof ApiError ? error.status : null;
      op.state = 'refused';
      op.httpStatus = status;
      op.lastError =
        late || status === null || status >= 500 ? NO_ANSWER : status === 429 ? BUSY : (error as Error).message;
      return { outcome: 'refused', ops };
    } finally {
      clearTimeout(timer);
    }
  }
  return { outcome: 'sent', ops };
}

/**
 * The line went partway through a batch: what is left is kept on this PC
 * under the keys it was sent with, so a request that did arrive is not done
 * twice. A post whose create went is kept with the id the server gave.
 */
async function keepRest(
  store: OutboxHolder,
  batch: readonly NewOperation[],
  ops: Operation[],
  from: number,
  made: ReadonlyMap<string, string>,
): Promise<Saved> {
  const rest: NewOperation[] = batch.slice(from).map((input, k) => {
    const op = ops[from + k]!;
    const dep = op.dependsOn ? made.get(op.dependsOn) : undefined;
    const { after, ...rest } = input;
    return {
      ...rest,
      id: op.id,
      path: dep ? input.path.replace('{dep}', encodeURIComponent(dep)) : input.path,
      ...(after !== undefined && after >= from ? { after: after - from } : {}),
    };
  });
  const kept = await keepOffline(store, rest);
  return { outcome: 'waiting', ops: [...ops.slice(0, from), ...kept.ops] };
}

/** Keeps work on this PC, listed on Offline work, to be sent when the line is back. */
async function keepOffline(store: OutboxHolder, batch: readonly NewOperation[]): Promise<Saved> {
  const ids = await enqueue(store, batch);
  await markKeptOffline(store, ids);
  tellOtherWindows({ type: 'outbox' });
  nudge();
  await refreshStatus(undefined);
  return outcomeOf(await readOutbox(store), ids);
}

/**
 * Sends work already saved, from this tab and now, and waits for the server's
 * answer: until it has all gone, some is refused, the line turns out to be
 * down, or `waitMs` passes.
 *
 * It is tried rather than judged: the "offline" flag can lag a server that is
 * back, and the tab that sends in the background may be one the browser has
 * put to sleep. Only work that gets no answer is kept as offline work.
 */
export async function awaitWork(ids: readonly string[], waitMs = WAIT_MS): Promise<Saved> {
  const store = holder;
  if (!store) return { outcome: 'not-kept', ops: [] };
  const pass = await Promise.race([
    runPass(store),
    new Promise<null>((resolve) => setTimeout(() => resolve(null), waitMs)),
  ]);
  if (pass) {
    tellWhatHappened(pass, new Set(ids));
    if (pass.sent > 0 || (pass.refusedOps?.length ?? 0) > 0) tellOtherWindows({ type: 'outbox' });
  }
  const result = outcomeOf(await readOutbox(store), ids);
  // No answer: it goes on Offline work, and stays there once it has gone.
  if (result.outcome === 'waiting') {
    await markKeptOffline(store, ids);
    tellOtherWindows({ type: 'outbox' });
    // The background sender takes it from here: when the line is back, or at its next try.
    nudge();
  }
  await refreshStatus(pass ? pass.stopped : undefined);
  return result;
}

/**
 * How long a save waits for the server before saying it is kept on this PC.
 * With a line the answer comes in well under a second; with none, the attempt
 * fails at once. This is for a line too slow to answer at all.
 */
const WAIT_MS = 10_000;

/**
 * Takes saved work back out, with whatever stands on it. For a screen still
 * open when the server refused it: the refusal is shown on the form, where it
 * can be put right, rather than queued up for Needs attention as well.
 */
export async function takeBack(id: string): Promise<void> {
  if (!holder) return;
  await withdraw(holder, id);
  await refreshStatus(undefined);
  tellOtherWindows({ type: 'outbox' });
}

/**
 * Sends refused work again, under a new retry key, after whatever was wrong
 * has been put right — elsewhere, or by `fixed`, what the form now holds.
 */
export async function retryWork(id: string, fixed?: Parameters<typeof sendAgain>[2]): Promise<string | null> {
  if (!holder) return null;
  const newId = await sendAgain(holder, id, fixed);
  pokeOutbox();
  await refreshStatus(undefined);
  return newId;
}

/** Clears the record of offline work that has since gone. Answers how many pieces went. */
export async function clearSentOfflineWork(): Promise<number> {
  if (!holder) return 0;
  const cleared = await clearSentOffline(holder, Date.now() + getServerTimeOffsetMs());
  await refreshStatus(undefined);
  // None of the counts the lists read by has moved, so they are told to read again.
  void queryClient.invalidateQueries({ queryKey: ['outbox'] });
  tellOtherWindows({ type: 'outbox' });
  return cleared;
}

/** What would go with this work if it were thrown away. */
export async function standingOn(id: string): Promise<Operation[]> {
  const ops = await savedWork();
  const gone = withEverythingOn(ops, id);
  return ops.filter((op) => gone.has(op.id) && op.id !== id);
}

/** The work this person has saved here, for the screens to show. */
export async function savedWork(): Promise<Operation[]> {
  return holder ? readOutbox(holder) : [];
}

function outcomeOf(all: readonly Operation[], ids: readonly string[]): Saved {
  const ops = ids.map((id) => all.find((op) => op.id === id)).filter((op): op is Operation => Boolean(op));
  const outcome = ops.some((op) => op.state === 'refused')
    ? 'refused'
    : ops.length === ids.length && ops.every((op) => op.state === 'sent')
      ? 'sent'
      : 'waiting';
  return { outcome, ops };
}

// While developing, a change to the sending code reloads the page. A hot swap
// would leave the sender already running on the old code, beside the new, and
// work saved offline would be sent by the old rules or not at all.
if (import.meta.hot) import.meta.hot.accept(() => window.location.reload());
